import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Mail, Shield, Bell, CreditCard, Sparkles, Gift, AlertTriangle, Check, Loader2, Phone, Settings, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Preference categories configuration
const PREFERENCE_CATEGORIES = {
  required: {
    title: 'Required Communications',
    description: 'These communications are required to service your policy and cannot be disabled.',
    items: [
      {
        id: 'policy_updates',
        label: 'Policy and Coverage Updates',
        description: 'Critical information about your policy terms, renewals, and coverage changes',
        icon: Shield,
        required: true
      },
      {
        id: 'claims_notifications',
        label: 'Claims and Account Notifications',
        description: 'Updates about your claims status, account activity, and important alerts',
        icon: Bell,
        required: true
      },
      {
        id: 'billing_reminders',
        label: 'Billing and Payment Reminders',
        description: 'Payment confirmations, billing statements, and premium due notices',
        icon: CreditCard,
        required: true
      }
    ]
  },
  optional: {
    title: 'Optional Communications',
    description: 'You can customize these preferences based on your interests.',
    items: [
      {
        id: 'product_updates',
        label: 'Product Updates and New Features',
        description: 'Learn about new coverage options, features, and service improvements',
        icon: Sparkles,
        required: false
      },
      {
        id: 'vintrakid_alerts',
        label: 'VINTrakID Insights and Alerts',
        description: 'Vehicle value tracking, market insights, and personalized recommendations',
        icon: Bell,
        required: false
      },
      {
        id: 'promotional_content',
        label: 'Promotional Offers and Partner Content',
        description: 'Exclusive deals, partner offers, and special promotions',
        icon: Gift,
        required: false
      }
    ]
  }
};

export default function EmailPreferencesPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState('');
  const [preferences, setPreferences] = useState({
    policy_updates: true,
    claims_notifications: true,
    billing_reminders: true,
    product_updates: true,
    vintrakid_alerts: true,
    promotional_content: true
  });
  const [changesSummary, setChangesSummary] = useState([]);

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('No token provided. Please use the link from your email.');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/email-preferences/validate?token=${encodeURIComponent(token)}`);
        const data = await response.json();

        if (data.valid) {
          setEmail(data.email);
          if (data.preferences) {
            setPreferences(data.preferences);
          }
        } else {
          setError(data.error || 'Invalid or expired link.');
        }
      } catch (err) {
        console.error('Token validation error:', err);
        setError('Unable to validate your request. Please try again or contact support.');
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  // Handle preference toggle
  const handlePreferenceChange = (id, checked) => {
    const item = [...PREFERENCE_CATEGORIES.required.items, ...PREFERENCE_CATEGORIES.optional.items]
      .find(p => p.id === id);
    
    if (item?.required) return;

    setPreferences(prev => ({
      ...prev,
      [id]: checked
    }));
  };

  // Save preferences
  const handleSavePreferences = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/email-preferences/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, preferences })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        setChangesSummary(data.changes_summary || []);
        toast.success('Preferences updated successfully');
      } else {
        throw new Error(data.detail || 'Failed to update preferences');
      }
    } catch (err) {
      console.error('Save preferences error:', err);
      toast.error(err.message || 'Failed to update preferences');
    } finally {
      setSaving(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex flex-col items-center justify-center" data-testid="email-prefs-loading">
        <Link to="/" className="inline-block mb-8">
          <img src="/losspay-header-logo.png" alt="LossPay" className="h-8 sm:h-10" />
        </Link>
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading your preferences...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="email-prefs-error">
        <div className="max-w-2xl mx-auto px-4 py-16">
          <div className="text-center mb-8">
            <Link to="/" className="inline-block mb-8">
              <img src="/losspay-header-logo.png" alt="LossPay" className="h-8 sm:h-10" />
            </Link>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="h-8 w-8 text-red-600" />
              </div>
              <h1 className="text-2xl font-semibold text-slate-900 mb-2">Unable to Load Preferences</h1>
              <p className="text-slate-600 mb-6">{error}</p>
              <div className="space-y-3">
                <p className="text-sm text-slate-500">Need help? Contact our support team:</p>
                <div className="flex items-center justify-center gap-6 text-sm">
                  <a href="mailto:support@losspay.com" className="text-blue-600 hover:text-blue-700 flex items-center gap-1">
                    <Mail className="h-4 w-4" />
                    support@losspay.com
                  </a>
                  <a href="tel:1-833-567-7729" className="text-blue-600 hover:text-blue-700 flex items-center gap-1">
                    <Phone className="h-4 w-4" />
                    1-833-LOSSPAY
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="email-prefs-success">
        <div className="max-w-2xl mx-auto px-4 py-16">
          <div className="text-center mb-8">
            <Link to="/" className="inline-block mb-8">
              <img src="/losspay-header-logo.png" alt="LossPay" className="h-8 sm:h-10" />
            </Link>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="h-8 w-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-semibold text-slate-900 mb-2">Preferences Updated</h1>
              <p className="text-slate-600 mb-6">Your email preferences have been saved successfully.</p>

              {changesSummary.length > 0 && (
                <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left">
                  <h3 className="text-sm font-medium text-slate-700 mb-2">Changes Made:</h3>
                  <ul className="space-y-1">
                    {changesSummary.map((change, index) => (
                      <li key={index} className="text-sm text-slate-600 flex items-center gap-2">
                        <Check className="h-4 w-4 text-green-500" />
                        {change}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-blue-800">
                  You will continue to receive essential policy, claims, and billing communications as required to service your policy.
                </p>
              </div>

              <Link to="/">
                <Button variant="outline" className="gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  Return to LossPay
                </Button>
              </Link>
            </div>
          </div>

          <ComplianceFooter />
        </div>
      </div>
    );
  }

  // Main preferences form
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="email-prefs-form">
      <div className="max-w-2xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6">
            <img src="/losspay-header-logo.png" alt="LossPay" className="h-8 sm:h-10" />
          </Link>
          <div className="flex items-center justify-center gap-2 mb-2">
            <Settings className="h-6 w-6 text-blue-600" />
            <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900">Email Preferences</h1>
          </div>
          <p className="text-slate-600 max-w-md mx-auto">
            Choose which types of emails you'd like to receive from LossPay.
          </p>
          {email && (
            <p className="text-sm text-slate-500 mt-2">
              Managing preferences for: <span className="font-medium">{email}</span>
            </p>
          )}
        </div>

        {/* Preferences Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
          {/* Required Communications Section */}
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center gap-2 mb-1">
              <Shield className="h-5 w-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-slate-900">
                {PREFERENCE_CATEGORIES.required.title}
              </h2>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              {PREFERENCE_CATEGORIES.required.description}
            </p>
            
            <div className="space-y-3">
              {PREFERENCE_CATEGORIES.required.items.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.id} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex-shrink-0 mt-1">
                      <Checkbox id={item.id} checked={true} disabled={true} className="cursor-not-allowed" />
                    </div>
                    <div className="flex-grow">
                      <Label htmlFor={item.id} className="text-sm font-medium text-slate-900 cursor-not-allowed flex items-center gap-2">
                        <Icon className="h-4 w-4 text-slate-400" />
                        {item.label}
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Required</span>
                      </Label>
                      <p className="text-xs text-slate-500 mt-1">{item.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Optional Communications Section */}
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="h-5 w-5 text-purple-600" />
              <h2 className="text-lg font-semibold text-slate-900">
                {PREFERENCE_CATEGORIES.optional.title}
              </h2>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              {PREFERENCE_CATEGORIES.optional.description}
            </p>
            
            <div className="space-y-3">
              {PREFERENCE_CATEGORIES.optional.items.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.id} className="flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-200 hover:border-blue-300 transition-colors">
                    <div className="flex-shrink-0 mt-1">
                      <Checkbox
                        id={item.id}
                        checked={preferences[item.id]}
                        onCheckedChange={(checked) => handlePreferenceChange(item.id, checked)}
                      />
                    </div>
                    <div className="flex-grow">
                      <Label htmlFor={item.id} className="text-sm font-medium text-slate-900 cursor-pointer flex items-center gap-2">
                        <Icon className="h-4 w-4 text-slate-400" />
                        {item.label}
                      </Label>
                      <p className="text-xs text-slate-500 mt-1">{item.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="p-6 bg-slate-50">
            <Button
              onClick={handleSavePreferences}
              disabled={saving}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-medium"
              data-testid="save-preferences-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                'Save Preferences'
              )}
            </Button>

            <div className="mt-4 text-center">
              <Link to={`/unsubscribe?token=${token}`} className="text-sm text-slate-600 hover:text-red-600 transition-colors">
                Or unsubscribe from all marketing emails
              </Link>
            </div>
          </div>
        </div>

        {/* Compliance Notice */}
        <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">
              <strong>Note:</strong> Required communications cannot be disabled as they contain important information about your policy, claims, and billing.
            </p>
          </div>
        </div>

        <ComplianceFooter />
      </div>
    </div>
  );
}

function ComplianceFooter() {
  return (
    <footer className="mt-12 pt-8 border-t border-slate-200">
      <div className="text-center space-y-4">
        <div className="text-sm text-slate-600">
          <p className="font-medium">LossPay Risk Technologies, Inc.</p>
          <p>16192 Coastal Highway, Lewes, Delaware 19958</p>
        </div>
        
        <div className="flex items-center justify-center gap-4 text-sm">
          <a href="tel:1-833-567-7729" className="text-blue-600 hover:text-blue-700">1-833-LOSSPAY</a>
          <span className="text-slate-300">|</span>
          <a href="mailto:support@losspay.com" className="text-blue-600 hover:text-blue-700">support@losspay.com</a>
        </div>

        <p className="text-xs text-slate-400">
          © {new Date().getFullYear()} LossPay Risk Technologies, Inc. All rights reserved.
        </p>
      </div>
    </footer>
  );
}
